import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, 
  GitBranch, 
  History, 
  Sparkles,
  Calendar,
  CheckCircle2,
  Eye,
  X,
  Layers,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const FunnelBuilderNew = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'history'
  const [loading, setLoading] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [activeICP, setActiveICP] = useState(null);
  
  // Form data
  const [funnelType, setFunnelType] = useState('advertising');
  
  // Generated result
  const [funnel, setFunnel] = useState(null);
  
  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Modal for detail view
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const funnelTypes = [
    { value: 'advertising', label: 'Embudo Publicitario (TOF-MOF-BOF)' },
    { value: 'sales', label: 'Embudo de Ventas' }
  ];

  useEffect(() => {
    loadBusinessAndICP();
  }, []);

  useEffect(() => {
    if (activeBusiness?.id) {
      loadHistory();
    }
  }, [activeBusiness]);

  const loadBusinessAndICP = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      setActiveBusiness(response.data.business);
      setActiveICP(response.data.icp);
    } catch (error) {
      console.error('Error loading business:', error);
      toast.error('Error al cargar empresa activa');
    }
  };

  const loadHistory = async () => {
    if (!activeBusiness?.id) {
      return;
    }
    
    try {
      setLoadingHistory(true);
      const response = await apiClient.get(`/funnels/${activeBusiness.id}`);
      setHistory(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!activeICP) {
      toast.error('Necesitas crear un ICP primero');
      navigate('/icp-generator');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/funnels/generate', {
        business_id: activeBusiness.id,
        funnel_type: funnelType
      });
      setFunnel(response.data);
      toast.success('Embudo generado exitosamente! 🎯');
      
      // Reload history to show the new funnel
      await loadHistory();
    } catch (error) {
      console.error('Error generating funnel:', error);
      toast.error(error.response?.data?.detail || 'Error al generar embudo');
    } finally {
      setLoading(false);
    }
  };

  const renderGenerateTab = () => (
    <div className="grid lg:grid-cols-3 gap-8">
      {/* Form */}
      <div>
        <Card className="border-0 shadow-lg sticky top-24">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="w-6 h-6 text-green-600" />
              Genera tu Embudo
            </CardTitle>
            <CardDescription>
              Estrategia completa de conversión basada en tu ICP
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeBusiness && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-900 mb-1">
                  📊 Empresa Activa
                </p>
                <p className="text-xs text-green-700">
                  {activeBusiness.business_description?.substring(0, 80)}...
                </p>
              </div>
            )}

            <form onSubmit={handleGenerate} className="space-y-6">
              <div>
                <Label htmlFor="funnelType">Tipo de Embudo *</Label>
                <Select value={funnelType} onValueChange={setFunnelType} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {funnelTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">✨ Qué Incluye:</h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Estrategia completa por etapas (TOF/MOF/BOF)</li>
                  <li>• Campañas recomendadas por fase</li>
                  <li>• Mensajes clave optimizados</li>
                  <li>• Creatividades sugeridas</li>
                  <li>• Acciones específicas por etapa</li>
                  <li>• Personalizado según tu ICP</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full py-6 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                disabled={loading || !activeICP}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Construyendo Embudo...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generar Embudo Completo
                  </>
                )}
              </Button>
              
              {!activeICP && (
                <p className="text-xs text-center text-red-600">
                  ⚠️ Necesitas crear un ICP primero
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <div className="lg:col-span-2">
        {funnel ? (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-green-600 to-emerald-600 text-white">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <CheckCircle2 className="w-8 h-8" />
                  Embudo Generado
                </CardTitle>
                <CardDescription className="text-green-100">
                  {funnel.funnel_type === 'advertising' ? 'Embudo Publicitario (TOF-MOF-BOF)' : 'Embudo de Ventas'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white/10 p-4 rounded-lg">
                  <p className="text-sm text-green-100 mb-2">
                    💡 <strong>Próximo Paso:</strong> Usa este embudo como guía para tus campañas
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Estrategia del Embudo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Stages */}
                {Object.entries(funnel.stages).map(([stageName, stageData], idx) => (
                  <div key={idx} className="border-l-4 border-green-500 pl-6 py-4 bg-green-50 rounded-r-lg">
                    <h3 className="text-lg font-bold text-slate-900 mb-3 capitalize">
                      {stageName.replace('_', ' ').toUpperCase()}
                    </h3>
                    
                    {stageData.description && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-700 font-medium">{stageData.description}</p>
                      </div>
                    )}

                    {stageData.campaigns && stageData.campaigns.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">📢 Campañas</h4>
                        <ul className="space-y-2">
                          {stageData.campaigns.map((campaign, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{campaign}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.messages && stageData.messages.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">💬 Mensajes Clave</h4>
                        <ul className="space-y-2">
                          {stageData.messages.map((message, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{message}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.creatives && stageData.creatives.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">🎨 Creatividades</h4>
                        <ul className="space-y-2">
                          {stageData.creatives.map((creative, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{creative}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.actions && stageData.actions.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">⚡ Acciones</h4>
                        <ul className="space-y-2">
                          {stageData.actions.map((action, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}

                {/* Recommendations */}
                {funnel.recommendations && funnel.recommendations.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                    <h3 className="text-lg font-bold text-green-900 mb-4">💡 Recomendaciones Estratégicas</h3>
                    <ul className="space-y-3">
                      {funnel.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-green-800">
                          <span className="flex-shrink-0 w-6 h-6 bg-green-300 rounded-full flex items-center justify-center text-xs font-bold text-green-900">
                            {idx + 1}
                          </span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-0 shadow-lg h-full flex items-center justify-center">
            <CardContent className="text-center py-20">
              <GitBranch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Listo para Generar</h3>
              <p className="text-slate-600 max-w-sm">
                Selecciona el tipo de embudo y haz clic en "Generar" para crear una estrategia completa de conversión
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  const openDetailModal = (funnel) => {
    setSelectedFunnel(funnel);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedFunnel(null);
  };

  const renderHistoryTab = () => (
    <div className="max-w-6xl mx-auto">
      <Card className="border-0 shadow-lg mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="w-6 h-6 text-green-600" />
                Historial de Embudos
              </CardTitle>
              <CardDescription>
                {history.length} embudo{history.length !== 1 ? 's' : ''} generado{history.length !== 1 ? 's' : ''}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {loadingHistory ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        </div>
      ) : history.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="text-center py-20">
            <GitBranch className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Sin Embudos Generados</h3>
            <p className="text-slate-600 mb-6">
              Genera tu primer embudo en la pestaña "Generar"
            </p>
            <Button onClick={() => setActiveTab('generate')}>
              Ir a Generar
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {history.map((item, index) => (
            <Card 
              key={item.id || index} 
              className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
              onClick={() => openDetailModal(item)}
              data-testid={`funnel-history-card-${index}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    item.funnel_type === 'advertising' 
                      ? 'bg-gradient-to-br from-green-500 to-emerald-500' 
                      : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  }`}>
                    <GitBranch className="w-5 h-5 text-white" />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDetailModal(item);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </Button>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-1">
                  {item.funnel_type === 'advertising' ? 'Embudo Publicitario' : 'Embudo de Ventas'}
                </h3>
                
                <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Layers className="w-3 h-3" />
                    {Object.keys(item.stages || {}).length} etapas
                  </span>
                </div>

                <div className="flex flex-wrap gap-1">
                  {Object.keys(item.stages || {}).slice(0, 3).map((stageName, idx) => (
                    <span 
                      key={idx} 
                      className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full capitalize"
                    >
                      {stageName.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFunnel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeDetailModal}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <GitBranch className="w-6 h-6" />
                    {selectedFunnel.funnel_type === 'advertising' ? 'Embudo Publicitario' : 'Embudo de Ventas'}
                  </h2>
                  <p className="text-green-100 text-sm mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedFunnel.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 rounded-full"
                  onClick={closeDetailModal}
                  data-testid="close-funnel-modal"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-6">
                {Object.entries(selectedFunnel.stages || {}).map(([stageName, stageData], idx) => (
                  <div key={idx} className="border-l-4 border-green-500 pl-6 py-4 bg-green-50 rounded-r-lg">
                    <h3 className="text-lg font-bold text-slate-900 mb-3 capitalize">
                      {stageName.replace('_', ' ').toUpperCase()}
                    </h3>
                    
                    {stageData.description && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-700 font-medium">{stageData.description}</p>
                      </div>
                    )}

                    {stageData.campaigns && stageData.campaigns.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">📢 Campañas</h4>
                        <ul className="space-y-2">
                          {stageData.campaigns.map((campaign, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{campaign}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.messages && stageData.messages.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">💬 Mensajes Clave</h4>
                        <ul className="space-y-2">
                          {stageData.messages.map((message, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{message}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.creatives && stageData.creatives.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">🎨 Creatividades</h4>
                        <ul className="space-y-2">
                          {stageData.creatives.map((creative, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{creative}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {stageData.actions && stageData.actions.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-2 text-sm">⚡ Acciones</h4>
                        <ul className="space-y-2">
                          {stageData.actions.map((action, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                              <span className="text-green-600 mt-0.5">•</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}

                {/* Recommendations */}
                {selectedFunnel.recommendations && selectedFunnel.recommendations.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                    <h3 className="text-lg font-bold text-green-900 mb-4">💡 Recomendaciones Estratégicas</h3>
                    <ul className="space-y-3">
                      {selectedFunnel.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-green-800">
                          <span className="flex-shrink-0 w-6 h-6 bg-green-300 rounded-full flex items-center justify-center text-xs font-bold text-green-900">
                            {idx + 1}
                          </span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50">
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Constructor de Embudos</h1>
            <p className="text-sm text-slate-600 mt-1">Diseña embudos completos de publicidad y ventas</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6 sm:mb-8">
            <TabsTrigger value="generate">
              <Sparkles className="w-4 h-4 mr-2" />
              Generar
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-2" />
              Historial ({history.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate">
            {renderGenerateTab()}
          </TabsContent>

          <TabsContent value="history">
            {renderHistoryTab()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default FunnelBuilderNew;
